import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RelatedControlTest } from '../components/qc-results/qc-results.component';
import {
  QcResultMetric,
  QcRangeCondition,
  QcRangeConditionType,
  QcResultRanges,
  QcResultCategory
} from '../interfaces/qcresults.interface';
import _ from 'lodash';
import { Store } from '@ngrx/store';
import { selectQCResults } from '../store/quality-check.selectors';
import { IgenomicsState } from '../../core/interfaces/genomics-state.interface';
import { setResultsActionCreator } from '../store/quality-check.actions';
import { getActiveRecord } from '@ispm/workflow/utils/store.utils';
import { catchError, map, take } from 'rxjs/operators';
import { ResourceRestService } from '@ispm/workflow/services/resource-rest.service';
import { Resources } from '@ispm/workflow/constants/resources.enum';
import { CURRENT_ORDER_ACTIONS } from '@ispm/dashboard/test/constants/current-order.constants';
import { currentOrderAllData } from '@ispm/dashboard/test/store/current-order.selector';

@Injectable()
export class QcService {
  static qcURLSuffix = '/test/getQcResultsTemplate';
  static relatedQcTestsURLSuffix = '/test/getRelatedControlTests';

  private store: any;

  constructor(
    private _http: ResourceRestService,
    store: Store<IgenomicsState>,
    private _store: Store<any>,
  ) {
    this.store = store.select(selectQCResults);
  }

  public getQcResults(toggleLoader: boolean, orderRevisionId: string): Observable<any> {
    const revisionId = ResourceRestService.getResourcesString(Resources.QCResult, Resources.FirstRevision);
    let currOrder: any;
    this._store.select(currentOrderAllData).pipe(take(1)).subscribe(currentOrderDetails => currOrder = currentOrderDetails);

    if (currOrder) {
      if (currOrder.isNewOrder) {
        return Observable.of({});
      }

      if (currOrder.qcResults && currOrder.revisionId === orderRevisionId) {
        this._store.dispatch(setResultsActionCreator(currOrder.qcResults));

        return Observable.of(currOrder.qcResults);
      }
    }

    const queryParams = {revisionId: revisionId};

    return this._http.get(QcService.qcURLSuffix, queryParams, toggleLoader).pipe(
        map(res => res.json()),
        map(results => this.formatResults(results)),
        map((results) => {
          this._store.dispatch({type: CURRENT_ORDER_ACTIONS.SET_QC_RESULTS, payload: results});
          this._store.dispatch(setResultsActionCreator(results));

          return results;
        }),
        catchError((err) => {
          console.log(err);
          return Observable.empty();
        })
      );
  }

  private formatResults(qcResults: any): { [key: string]: QcResultCategory } {
    const results: { [key: string]: QcResultCategory } = { };

    _.forIn(qcResults, (qcMetrics, qcCategory: string) => {
      results[qcCategory] = {
        metrics: qcMetrics
      };

      results[qcCategory].metrics.forEach(qcMetric => {
        qcMetric.withinFailRange = false;
        qcMetric.withinWarningRange = false;

        if (qcMetric.ranges) {
          if (qcMetric.ranges.fail) {
            const failRangesResult: RangesResult = this.checkQcRange(qcMetric, qcMetric.ranges.fail);

            if (failRangesResult.withinRange) {
              qcMetric.withinFailRange = true;
              qcMetric.failMessage = failRangesResult.message;

              if (!results[qcCategory].containsFailures) {
                results[qcCategory].containsFailures = true;
                results[qcCategory].failureMessage = failRangesResult.message;
              }
            }
          }

          if (qcMetric.ranges.warning) {
            const warningRangesResult: RangesResult = this.checkQcRange(qcMetric, qcMetric.ranges.warning);

            if (warningRangesResult.withinRange) {
              qcMetric.withinWarningRange = true;
              qcMetric.warningMessage = warningRangesResult.message;

              if (!results[qcCategory].containsWarnings) {
                results[qcCategory].containsWarnings = true;
                results[qcCategory].warningMessage = warningRangesResult.message;
              }
            }
          }
        }
      });
    });

    return results;
  }

  private checkQcRange(qcResultMetric: QcResultMetric, qcResultRanges: QcResultRanges): RangesResult {
    let message = '';

    for (let rangeIndex = 0; rangeIndex < qcResultRanges.conditions.length; rangeIndex++) {
      const condition = qcResultRanges.conditions[rangeIndex];

      if (!this.checkQcWithinRangeCondition(qcResultMetric, condition)) {
        return {
          withinRange: false
        };
      } else {
        message = condition.indicatorMessage ? condition.indicatorMessage : null;
      }
    }

    return {
      message: message,
      withinRange: true
    };
  }

  private checkQcWithinRangeCondition(qcResultMetric: QcResultMetric, qcRangeCondition: QcRangeCondition, inverted: boolean = false): boolean {
    const metricValue: number = Number.parseFloat(qcResultMetric.value);

    switch (qcRangeCondition.type) {
      case QcRangeConditionType.STATIC_VALUE:
        if (qcRangeCondition.values) {
          return qcRangeCondition.values.indexOf(qcResultMetric.value) == -1;
        } else if (qcRangeCondition.value) {
          return qcRangeCondition.value == qcResultMetric.value;
        }
        return inverted;
      case QcRangeConditionType.LOW:
        if (Number.isNaN(metricValue)) {
          return qcRangeCondition.value == qcResultMetric.value;
        } else {
          const qcRangeValue: number = Number.parseFloat(qcRangeCondition.value);
          return metricValue > qcRangeValue;
        }
      case QcRangeConditionType.HIGH:
        if (Number.isNaN(metricValue)) {
          return qcRangeCondition.value == qcResultMetric.value;
        } else {
          const qcRangeValue: number = Number.parseFloat(qcRangeCondition.value);
          return metricValue < qcRangeValue;
        }
    }
  }

  public getRelatedQCTests(workflowId: string, testType: string): Observable<RelatedControlTest[]> {
    return this._http.get(QcService.relatedQcTestsURLSuffix, { workflowId: workflowId, testType: testType }, true)
      .pipe(
        map(res => res.json())
      )
  }

  public getActiveResults() {
    return this.store.flatMap((state: any) => getActiveRecord(state));
  }

  public loadControlSampleStatusesConfig() {
    return this._http.get('valueset/getNavicanControlTestStatuses', { }, true)
      .pipe(
        map(res => res.json())
      )
  }
}

interface RangesResult {
  withinRange: boolean;
  message?: string;
}
